/**
 * emailTemplates.js — Email template engine with merge tags
 *
 * Provides default templates for common business emails and a merge-tag
 * interpolation engine. Templates are stored in settings.data.emailTemplates
 * and can be customized per-company in the Settings → Email Templates tab.
 *
 * Merge tags use {{tagName}} syntax and are replaced with entity data.
 *
 * Usage:
 *   const { subject, body } = buildEmail('invoice_sent', { invoice, job, settings });
 *   window.location.href = buildMailtoUrl(toEmail, subject, body);
 */

import { normalizeSnapshot, deptLeads } from './calcEngine';
import { renderTermsAsText } from './quoteTerms';

// ── Template definitions ────────────────────────────────────────────────────

export const TEMPLATE_TYPES = [
  {
    id: 'quote_sent',
    label: 'Quote Sent',
    description: 'Sent when sharing a new or revised quote with a client',
    defaultSubject: 'Quote #{{quoteNumber}}{{quoteSuffix}} · {{jobTitle}}',
    defaultBody: `Hi {{clientName}},

{{quoteIntro}}

Quote #{{quoteNumber}} | {{quoteTotal}}
{{discountInfo}}
{{serviceSchedule}}
Full details, deliverables, and our terms & conditions are in the attached PDF — please take a moment to review.

{{closingCta}}

{{closingLine}}`,
  },
  {
    id: 'invoice_sent',
    label: 'Invoice Sent',
    description: 'Sent when sharing an invoice with a client',
    defaultSubject: 'Invoice #{{invoiceNumber}} · {{jobTitle}}',
    defaultBody: `Hi {{clientName}},

Attached is Invoice #{{invoiceNumber}} for {{jobTitle}}.

Amount: {{invoiceAmount}}
Due: {{dueDate}}

{{paymentOptions}}

Let me know if you have any questions.`,
  },
  {
    id: 'deposit_invoice_sent',
    label: 'Deposit Invoice Sent',
    description: 'Sent when sharing a deposit invoice with a client',
    defaultSubject: 'Deposit Invoice #{{invoiceNumber}} · {{jobTitle}}',
    defaultBody: `Hi {{clientName}},

Attached is your deposit invoice for {{jobTitle}}.

Project Total: {{projectTotal}}
Deposit Due: {{invoiceAmount}}
Due: {{dueDate}}

{{paymentOptions}}

Once the deposit is in, we'll lock your date on the calendar.`,
  },
  {
    id: 'balance_invoice_sent',
    label: 'Balance Invoice Sent',
    description: 'Sent when sharing a balance invoice with a client',
    defaultSubject: 'Balance Invoice #{{invoiceNumber}} · {{jobTitle}}',
    defaultBody: `Hi {{clientName}},

Attached is your balance invoice for {{jobTitle}}.

Project Total: {{projectTotal}}
Deposit Paid: {{depositPaid}}
Balance Due: {{invoiceAmount}}
Due: {{dueDate}}

{{paymentOptions}}

Let me know if you have any questions.`,
  },
  {
    id: 'consolidated_invoice_sent',
    label: 'Bulk Invoice Sent',
    description: 'Sent when sharing one invoice that covers multiple jobs',
    defaultSubject: 'Invoice #{{invoiceNumber}} · {{jobCount}} properties',
    defaultBody: `Hi {{clientName}},

Attached is Invoice #{{invoiceNumber}} covering {{jobCount}} properties:

{{propertyList}}

Total: {{invoiceAmount}}
Due: {{dueDate}}

{{paymentOptions}}

The attached PDF has the full per-property breakdown. Let me know if you have any questions.`,
  },
  {
    id: 'payment_received',
    label: 'Payment Received',
    description: 'Confirmation sent after recording a payment',
    defaultSubject: 'Payment received · Invoice #{{invoiceNumber}}',
    defaultBody: `Hi {{clientName}},

Payment of {{paymentAmount}} received for Invoice #{{invoiceNumber}} — thank you!

{{balanceSummary}}`,
  },
  {
    id: 'booking_confirmed',
    label: 'Booking Confirmed',
    description: 'Sent when a job is moved to "confirmed" status',
    defaultSubject: "You're booked · {{jobTitle}}",
    defaultBody: `Hi {{clientName}},

Your shoot for {{jobTitle}} is confirmed.
{{serviceSchedule}}{{prepChecklist}}
If anything changes, just let me know.`,
  },
  {
    id: 'delivery_complete',
    label: 'Delivery Complete',
    description: 'Sent when a job is marked as "delivered"',
    defaultSubject: 'Your deliverables are ready · {{jobTitle}}',
    defaultBody: `Hi {{clientName}},

Your project is complete. Here's the link to your files:

{{deliveryLink}}

Everything's ready to download. If anything needs a revision, let me know within 7 days.
{{referralAsk}}
Thank you for working with us.`,
  },
  {
    id: 'delivery_partial',
    label: 'Partial Delivery',
    description: 'Sent when some (not all) deliverables have been sent',
    defaultSubject: 'Partial delivery · {{jobTitle}}',
    defaultBody: `Hi {{clientName}},

Wanted to get you started — here's what's ready so far for {{jobTitle}}:

{{deliveredItems}}

That's {{deliveredCount}} of {{totalCount}} deliverables. The rest are in progress and I'll send them as soon as they're done.`,
  },
  {
    id: 'payment_reminder',
    label: 'Payment Reminder',
    description: 'Gentle reminder for overdue invoices',
    defaultSubject: 'Friendly reminder · Invoice #{{invoiceNumber}}',
    defaultBody: `Hi {{clientName}},

Quick reminder that Invoice #{{invoiceNumber}} for {{invoiceAmount}} was due on {{dueDate}}.

Remaining balance: {{balanceDue}}

{{paymentOptions}}

If you've already sent it, please disregard. Otherwise, I'd appreciate it at your earliest convenience.`,
  },
  {
    id: 'follow_up',
    label: 'Quote Follow-Up',
    description: 'Follow-up when a client has not responded to a quote',
    defaultSubject: 'Following up on your quote | {{jobTitle}}',
    defaultBody: `Hi {{clientName}},

Wanted to check in on the quote I sent over for {{jobTitle}} (Quote #{{quoteNumber}}).

If you're ready to move forward, I can get a date on the calendar. If the scope or pricing needs adjusting, happy to work through it.

Let me know where things stand.`,
  },
  {
    id: 'shoot_details',
    label: 'Shoot Details',
    description: 'Sent to client before the shoot with prep instructions',
    defaultSubject: 'Shoot details | {{jobTitle}}',
    defaultBody: `Hi {{clientName}},

Here's what's scheduled for {{jobTitle}}:
{{serviceSchedule}}{{prepChecklist}}
We'll plan to arrive at the scheduled time. If anything comes up or you need to adjust, just let me know.

Looking forward to it!`,
  },
  {
    id: 'void_notification',
    label: 'Void / Cancel Notification',
    description: 'Sent to client when a job is voided or cancelled',
    defaultSubject: 'Job cancelled | {{jobTitle}}',
    defaultBody: `Hi {{clientName}},

I wanted to let you know that the project for {{jobTitle}} has been cancelled.

If you'd like to reschedule or have any questions, don't hesitate to reach out.`,
  },
  {
    id: 'crew_notification',
    label: 'Crew Notification',
    description: 'Sent to crew members with shoot assignment details',
    defaultSubject: 'Shoot assignment | {{jobTitle}}',
    defaultBody: `Hey team,

Here are the details for an upcoming shoot:

Job: {{jobTitle}}
Type: {{jobType}}
Client: {{clientName}}
Location: {{location}}
{{crewServiceSchedule}}
{{crewRoster}}
{{jobNotes}}
Let me know if you have any questions or conflicts.`,
  },
];

// ── Available merge tags ────────────────────────────────────────────────────

export const MERGE_TAGS = [
  { tag: '{{companyName}}', description: 'Your company name from Settings' },
  { tag: '{{companyAddress}}', description: 'Your company address' },
  { tag: '{{companyPhone}}', description: 'Your company phone' },
  { tag: '{{clientName}}', description: 'Client/contact name' },
  { tag: '{{clientEmail}}', description: 'Client email address' },
  { tag: '{{jobTitle}}', description: 'Job title' },
  { tag: '{{shootDate}}', description: 'Shoot date' },
  { tag: '{{location}}', description: 'Job location' },
  { tag: '{{invoiceNumber}}', description: 'Invoice number' },
  { tag: '{{invoiceAmount}}', description: 'Invoice total amount' },
  { tag: '{{balanceDue}}', description: 'Remaining balance on invoice' },
  { tag: '{{balanceSummary}}', description: 'Balance status line (shows remaining or paid in full)' },
  { tag: '{{dueDate}}', description: 'Invoice due date' },
  { tag: '{{payTerms}}', description: 'Payment terms (e.g., Net 30)' },
  { tag: '{{paymentAmount}}', description: 'Most recent payment amount' },
  { tag: '{{paymentLink}}', description: 'Payment link from Settings' },
  { tag: '{{quoteNumber}}', description: 'Quote number' },
  { tag: '{{quoteTotal}}', description: 'Quote total' },
  { tag: '{{quoteExpiry}}', description: 'Quote expiry date' },
  { tag: '{{deliveredCount}}', description: 'Number of delivered items' },
  { tag: '{{totalCount}}', description: 'Total number of deliverable items' },
  { tag: '{{deliveredItems}}', description: 'List of delivered item descriptions' },
  { tag: '{{deliveryLink}}', description: 'Link to download deliverables' },
  { tag: '{{serviceSchedule}}', description: 'Auto-generated service & schedule details' },
  { tag: '{{prepChecklist}}', description: 'Shoot prep checklist (real estate only)' },
  { tag: '{{referralAsk}}', description: 'Referral request (real estate only)' },
  { tag: '{{senderName}}', description: 'Full name of the logged-in user' },
  { tag: '{{quoteSuffix}}', description: '" (Revised)" when quote is a revision, empty otherwise' },
  { tag: '{{quoteIntro}}', description: 'Auto-generated intro (adapts for initial vs revised quotes)' },
  { tag: '{{subtotal}}', description: 'Quote subtotal before discount' },
  { tag: '{{discountInfo}}', description: 'Discount line (e.g. "Discount: 10% (-$250.00)") — empty if no discount' },
  { tag: '{{closingCta}}', description: 'Rotating call-to-action line (adapts for initial vs revised quotes)' },
  { tag: '{{closingLine}}', description: 'Rotating friendly sign-off line (adapts for initial vs revised quotes)' },
  { tag: '{{crewServiceSchedule}}', description: 'Service schedule without prices (for crew emails)' },
  { tag: '{{crewRoster}}', description: 'Crew members with roles (from pay split)' },
  { tag: '{{jobType}}', description: 'Job type (e.g. Real Estate Video, FPV Tour)' },
  { tag: '{{jobNotes}}', description: 'Notes attached to the job' },
  { tag: '{{invoiceLink}}', description: 'Xero online invoice link (or payment link fallback)' },
  { tag: '{{projectTotal}}', description: 'Full project revenue amount' },
  { tag: '{{depositPaid}}', description: 'Paid deposit amount (or $0.00 if unpaid/none)' },
  { tag: '{{paymentLine}}', description: 'Single-line payment link (legacy — prefer {{paymentOptions}} for invoices)' },
  { tag: '{{paymentOptions}}', description: 'Two-option payment block: online card link (with fee note) + Zelle address' },
  { tag: '{{zelleAddress}}', description: 'Zelle email/phone for no-fee payment' },
  { tag: '{{cardFeePct}}', description: 'Card processing fee percentage (e.g. 3)' },
  { tag: '{{quoteTerms}}', description: 'Optional: full terms & conditions block (labels + bodies). Not in the default quote email — terms are in the attached PDF. Add this tag if you want them inline.' },
  { tag: '{{jobCount}}', description: 'Number of jobs covered by a bulk invoice' },
  { tag: '{{propertyList}}', description: 'Bulk invoice property list — one line per covered job with its amount' },
];

// ── Helpers ─────────────────────────────────────────────────────────────────

function fmtC(amount) {
  const n = parseFloat(amount);
  if (isNaN(n)) return '$0.00';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
}

// TZ-safe date formatter.
// Parsing a bare 'YYYY-MM-DD' with `new Date(str)` treats it as UTC midnight,
// which renders as the *previous* day in America/Los_Angeles. Appending
// 'T00:00:00' anchors it to local midnight so the calendar day is preserved.
// Returns '' (not '—') for empty/invalid so email bodies stay clean.
function fmtD(date) {
  if (!date) return '';
  const s = String(date);
  const dateOnly = s.includes('T') ? s.split('T')[0] : s;
  const d = new Date(dateOnly + 'T00:00:00');
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// ── Template interpolation ──────────────────────────────────────────────────

/**
 * Build a merge-tag context from available entity data
 */
// ── Service schedule & prep checklist builders ─────────────────────────────

function fmtTime(t) {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  if (isNaN(h)) return t;
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return m ? `${h12}:${String(m).padStart(2, '0')} ${ampm}` : `${h12} ${ampm}`;
}

function getShootDate(line) {
  const sched = line.schedule;
  if (sched && sched.length) return sched[0].date || '';
  return line.shootDate || '';
}

function getShootTime(line) {
  const sched = line.schedule;
  if (sched && sched.length) return sched[0].time || '';
  return line.shootTime || '';
}

function getShootLocation(line) {
  const sched = line.schedule;
  if (sched && sched.length) return sched[0].location || '';
  return line.location || '';
}

function buildServiceSchedule(job, { showPrices = true } = {}) {
  const snap = job?.calcSnapshot;
  // Add-ons are billed but kept out of deliverableLines (sid invariant); they
  // carry no schedule, so they group under the no-shoot section.
  const lines = [...(snap?.deliverableLines || []), ...(snap?.addOnLines || [])];
  if (!lines.length) return '';

  // ── Shoot section: group services by shoot date ──
  // Collect all (date, time, location) → [service {name, price}]
  const shootGroups = new Map(); // key = date string, value = { date, time, location, services[] }
  const noShootServices = [];

  lines.forEach((line) => {
    const desc = line.desc || 'Service';
    const price = showPrices && line.amount ? ` (${fmtC(line.amount)})` : '';
    const label = `${desc}${price}`;
    const sched = line.schedule;

    if (sched && sched.length > 1) {
      // Multi-day shoot — each day is its own group
      sched.forEach((s) => {
        if (s.date) {
          const key = s.date;
          if (!shootGroups.has(key)) {
            shootGroups.set(key, { date: s.date, time: s.time, location: s.location, services: [] });
          }
          shootGroups.get(key).services.push(label);
        }
      });
    } else {
      const d = getShootDate(line);
      if (d) {
        const key = d;
        if (!shootGroups.has(key)) {
          shootGroups.set(key, { date: d, time: getShootTime(line), location: getShootLocation(line), services: [] });
        }
        shootGroups.get(key).services.push(label);
      } else {
        noShootServices.push(label);
      }
    }
  });

  const parts = [];

  // Shoot schedule
  if (shootGroups.size > 0) {
    const shootLines = [];
    for (const g of shootGroups.values()) {
      let header = fmtD(g.date);
      if (g.time) header += ` at ${fmtTime(g.time)}`;
      if (g.location) header += ` at ${g.location}`;
      if (g.services.length === 1) {
        shootLines.push(`• ${g.services[0]}, ${header}`);
      } else {
        shootLines.push(`${header}`);
        g.services.forEach((s) => shootLines.push(`  • ${s}`));
      }
    }
    parts.push('Shoot schedule:\n' + shootLines.join('\n'));
  }

  // Services with no shoot date
  if (noShootServices.length) {
    parts.push('Services:\n' + noShootServices.map((s) => `• ${s}`).join('\n'));
  }

  // ── Deliverables section: group by delivery date ──
  const deliverables = lines.filter((l) => l.deliveryDate);
  if (deliverables.length) {
    const delGroups = new Map();
    deliverables.forEach((line) => {
      const desc = line.desc || 'Deliverable';
      let dateKey = line.deliveryDate;
      let timeStr = '';
      if (line.deliveryTime && line.deliveryTime !== 'EOD') {
        timeStr = ` by ${fmtTime(line.deliveryTime)}`;
        dateKey += timeStr;
      }
      if (!delGroups.has(dateKey)) {
        delGroups.set(dateKey, { date: line.deliveryDate, timeStr, items: [] });
      }
      delGroups.get(dateKey).items.push(desc);
    });

    const delLines = [];
    for (const g of delGroups.values()) {
      const header = fmtD(g.date) + g.timeStr;
      if (g.items.length === 1) {
        delLines.push(`• ${g.items[0]}, ${header}`);
      } else {
        delLines.push(`${header}`);
        g.items.forEach((s) => delLines.push(`  • ${s}`));
      }
    }
    parts.push('Deliverables:\n' + delLines.join('\n'));
  }

  return parts.length ? '\n' + parts.join('\n\n') + '\n' : '';
}

function isRealEstate(job) {
  const jt = (job?.jobType || job?.type || '').toLowerCase();
  return jt.includes('real estate');
}

function buildPrepChecklist(job) {
  if (!isRealEstate(job)) return '';
  return `\nA few things to help us get the best results:\n• Cars moved out of the driveway and off the street if possible\n• Interior lights on, blinds and curtains open\n• Pools and spas uncovered\n• Pets secured during the shoot\n`;
}

function firstName(full) {
  return (full || '').split(/\s+/)[0] || '';
}

function buildReferralAsk(job) {
  if (!isRealEstate(job)) return '';
  return `\nIf you know any agents who could use aerial or video content for their listings, I'd really appreciate the referral. It's the best compliment we can get.\n`;
}

function isRevision(job) {
  const qn = job?.calcSnapshot?.quoteNumber || '';
  return /-R\d+$/.test(qn);
}

function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

const INITIAL_CTAS = [
  "Just say the word and I'll send a deposit invoice to lock in the date.",
  "Once you're good to go, I'll send a deposit invoice to get you on the calendar.",
  "When you're ready, I'll get a deposit invoice over and we'll hold the date.",
  "Let me know and I'll send a deposit invoice to secure your spot.",
  "Ready to lock it in? I'll send over a deposit invoice to hold the date.",
  "If everything looks good, I'll get a deposit invoice sent over to reserve the date.",
  "Whenever you're ready, just let me know and I'll send a deposit invoice your way.",
];

const REVISED_CTAS = [
  "Let me know if this looks right and I'll get a deposit invoice over to lock in the date.",
  "If we're good on the changes, I'll send over a deposit invoice to hold the date.",
  "Once you've had a chance to review, I'll send a deposit invoice your way.",
  "Let me know if the revisions look right and I'll get you on the calendar.",
  "If this works for you, I'll send a deposit invoice to secure the date.",
  "Happy with the changes? Just say the word and I'll send a deposit invoice.",
  "When you're ready, I'll get a deposit invoice over and we'll hold the date.",
];

const INITIAL_CLOSINGS = [
  "Happy to adjust anything if you need.",
  "Any questions, just let me know!",
  "Let me know if you'd like to tweak anything.",
  "Feel free to reach out if anything needs adjusting.",
  "Don't hesitate to reach out with any questions.",
  "If anything needs changing, I'm happy to work through it.",
  "Looking forward to working together!",
];

const REVISED_CLOSINGS = [
  "Let me know if you'd like any other adjustments.",
  "Happy to keep tweaking until it's right.",
  "If anything else needs changing, just let me know.",
  "Hope this version works better for you!",
  "Feel free to reach out if you want to adjust further.",
  "Let me know how this looks.",
  "Always happy to fine-tune things.",
];

function buildRevisionTags(job) {
  const revised = isRevision(job);
  return {
    quoteSuffix: revised ? ' (Revised)' : '',
    quoteIntro: revised
      ? `I've updated the quote for media services:`
      : `Here's your quote for media services:`,
    closingCta: pick(revised ? REVISED_CTAS : INITIAL_CTAS),
    closingLine: pick(revised ? REVISED_CLOSINGS : INITIAL_CLOSINGS),
  };
}

function buildDiscountTags(job) {
  const discPct = parseFloat(job?.discountPct) || 0;
  const discAmt = parseFloat(job?.discountAmt ?? job?.discount) || 0;
  const revenue = parseFloat(job?.revenue) || 0;
  const subtotal = discAmt > 0 ? revenue + discAmt : revenue;
  let discountInfo = '';
  if (discAmt > 0) {
    discountInfo = discPct > 0
      ? `Discount: ${discPct}% (-${fmtC(discAmt)})`
      : `Discount: -${fmtC(discAmt)}`;
  }
  return {
    subtotal: fmtC(subtotal),
    discountInfo,
  };
}

function buildCrewRoster(job, roster) {
  const snap = job?.calcSnapshot;
  if (!snap) return '';
  const ns = normalizeSnapshot(snap);
  const lines = [];
  const resolveName = (rosterId, fallbackName) => {
    if (!roster?.length || !rosterId) return fallbackName || 'Unknown';
    const member = roster.find((r) => r.id === rosterId);
    return member?.name || fallbackName || 'Unknown';
  };
  ns.originators?.forEach((o) => {
    lines.push(`• ${resolveName(o.rosterId, o.name)} — Originator`);
  });
  deptLeads(ns.prod).forEach((l) => {
    lines.push(`• ${resolveName(l.rosterId, l.name)} — Production Lead`);
  });
  ns.prod?.hourly?.forEach((h) => {
    lines.push(`• ${resolveName(h.rosterId, h.name)} — Production (Hourly)`);
  });
  deptLeads(ns.post).forEach((l) => {
    lines.push(`• ${resolveName(l.rosterId, l.name)} — Post-Production Lead`);
  });
  ns.post?.hourly?.forEach((h) => {
    lines.push(`• ${resolveName(h.rosterId, h.name)} — Post-Production (Hourly)`);
  });
  return lines.length ? 'Crew:\n' + lines.join('\n') : '';
}

function buildContext({ invoice, job, contact, settings, quote, delivery, senderName, roster, allInvoices, depositRequest } = {}) {
  const s = settings?.data || {};
  const revisionTags = buildRevisionTags(job);
  const fullClient = contact?.name || invoice?.client || job?.clientName || '';
  const link = invoice?.xeroOnlineUrl || s.paymentLink || '';
  const depositInv = (allInvoices || []).find((i) => i.invoiceType === 'deposit' && i.status !== 'void');
  const depositPaidAmt = (depositInv && depositInv.status === 'paid') ? (parseFloat(depositInv.amount) || 0) : 0;
  const cardFeePct = Number(s.paymentCardFeePct ?? 3);
  const zelleAddress = s.paymentZelleAddress || 'contact@rotoflight.com';
  // When a deposit invoice rides along with the quote, the closing CTA (which
  // otherwise *promises* to send a deposit invoice) becomes a direct pay-now
  // block — card link + Zelle, mirroring the invoice payment options. The card
  // link is a placeholder swapped for the real Xero URL at send time
  // (see quoteSend.executeQuoteSend).
  if (depositRequest) {
    revisionTags.closingCta =
      `To lock in your date, here's your deposit invoice for ${depositRequest.amount}:\n\n` +
      `Pay online (card): ${depositRequest.placeholder}  — a ${cardFeePct}% card fee applies.\n` +
      `Prefer no fee? Pay via Zelle: ${zelleAddress}`;
  }
  const paymentOptions = [
    link
      ? `Pay online (card): ${link}  — a ${cardFeePct}% card fee applies.`
      : `Pay online (card): a link will be included once your invoice is synced.  — a ${cardFeePct}% card fee applies.`,
    `Prefer no fee? Pay via Zelle: ${zelleAddress}`,
  ].join('\n');
  // Bulk invoice tags — compact one-line-per-property list (the full service
  // breakdown lives in the PDF; mailto URLs truncate around ~2000 chars).
  const bulkItems = Array.isArray(invoice?.lineItems) ? invoice.lineItems : [];
  return {
    companyName: s.companyName || 'Roto Flight',
    companyAddress: s.companyAddress || '',
    companyPhone: s.companyPhone || '',
    senderName: senderName || s.senderName || s.companyName || 'Roto Flight',
    clientName: firstName(fullClient),
    clientEmail: contact?.email || job?.contactEmail || '',
    jobTitle: job?.title || '',
    shootDate: fmtD(job?.shootDate),
    location: job?.location || '',
    invoiceNumber: invoice?.number || '',
    invoiceAmount: fmtC(invoice?.amount),
    balanceDue: fmtC((parseFloat(invoice?.amount) || 0) - (parseFloat(invoice?.paidAmount) || 0)),
    balanceSummary: (() => {
      const bal = (parseFloat(invoice?.amount) || 0) - (parseFloat(invoice?.paidAmount) || 0);
      return bal > 0 ? `Remaining balance: ${fmtC(bal)}` : 'Your account is paid in full.';
    })(),
    dueDate: fmtD(invoice?.dueDate),
    payTerms: invoice?.terms || s.payTerms || 'Net 30',
    paymentAmount: fmtC(invoice?.payments?.length ? invoice.payments[invoice.payments.length - 1].amount : 0),
    paymentLink: link,
    quoteNumber: quote?.number || job?.calcSnapshot?.quoteNumber || '',
    quoteTotal: fmtC(quote?.total || job?.revenue),
    quoteExpiry: fmtD(quote?.expiryDate),
    deliveredCount: delivery?.deliveredCount ?? '',
    totalCount: delivery?.totalCount ?? '',
    deliveredItems: delivery?.deliveredItems ?? '',
    deliveryLink: job?.deliveryLinks || job?.deliveryLink || job?.delivery_link || '',
    serviceSchedule: buildServiceSchedule(job),
    crewServiceSchedule: buildServiceSchedule(job, { showPrices: false }),
    prepChecklist: buildPrepChecklist(job),
    referralAsk: buildReferralAsk(job),
    crewRoster: buildCrewRoster(job, roster),
    jobType: job?.jobType || job?.type || '',
    jobNotes: job?.notes ? `Notes: ${job.notes}` : '',
    invoiceLink: link,
    projectTotal: fmtC(job?.revenue),
    depositPaid: fmtC(depositPaidAmt),
    paymentLine: link ? `You can view and pay your invoice here:
${link}` : '',
    paymentOptions,
    zelleAddress,
    cardFeePct,
    jobCount: bulkItems.length || '',
    propertyList: bulkItems.length
      ? bulkItems.map((li) => `• ${li.location || li.title || 'Property'} — ${fmtC(li.amount)}`).join('\n')
      : '',
    quoteTerms: renderTermsAsText(quote || job, settings),
    ...revisionTags,
    ...buildDiscountTags(job),
  };
}

/**
 * Interpolate merge tags in a template string
 */
function interpolate(template, context) {
  return template.replace(/\{\{(\w+)\}\}/g, (match, key) => {
    return context[key] !== undefined ? context[key] : match;
  });
}

/**
 * Get the template (custom from settings or default)
 */
function getTemplate(templateId, settings) {
  const customTemplates = settings?.data?.emailTemplates || {};
  const custom = customTemplates[templateId];
  const def = TEMPLATE_TYPES.find((t) => t.id === templateId);

  return {
    subject: custom?.subject || def?.defaultSubject || '',
    body: custom?.body || def?.defaultBody || '',
  };
}

// ── Public API ──────────────────────────────────────────────────────────────

/**
 * Build an email (subject + body) from a template with merged data
 */
export function buildEmail(templateId, data = {}) {
  const template = getTemplate(templateId, data.settings);
  const context = buildContext(data);

  const rawBody = interpolate(template.body, context);
  // Collapse runs of 3+ newlines (from empty merge tags) into double newlines
  const body = rawBody.replace(/\n{3,}/g, '\n\n');

  return {
    subject: interpolate(template.subject, context),
    body,
  };
}

/**
 * Build a mailto: URL with pre-filled subject and body
 */
export function buildMailtoUrl(toEmail, subject, body) {
  const parts = [];
  if (subject) parts.push(`subject=${encodeURIComponent(subject)}`);
  if (body) parts.push(`body=${encodeURIComponent(body)}`);
  return `mailto:${toEmail || ''}?${parts.join('&')}`;
}

/**
 * Open the user's email client with a pre-filled email
 */
export function openEmail(templateId, { toEmail, ...data } = {}) {
  const { subject, body } = buildEmail(templateId, data);
  const url = buildMailtoUrl(toEmail, subject, body);
  window.location.href = url;
}
